import { forwardRef, useImperativeHandle, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
    IPortfolioItemReportFragment,
    ISkillListItemFragment,
} from 'graphql/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { IGlobalDrawerType } from 'common/types';
import { TableCell } from 'common/components/Table';
import { withSxProp } from 'common/utils/props';
import { PortfolioItemProgressScoreChip } from 'user/components/PortfolioItemProgressScoreChip';
import { CreateSkillPortfolioItemDialog } from 'skill/components/CreateSkillPortfolioItemDialog';

interface IProps extends React.ComponentProps<typeof TableCell> {
    report?: IPortfolioItemReportFragment;
    skill: ISkillListItemFragment;
    disableClick?: boolean;
    userId: string;
    userName: string;
    onClearCache?: () => void;
    onUpdate?: (portfolioItemId?: string) => void;
}

export type TMatrixCellRef = { handleClick: () => void };

export const SkillReportMatrixCell = forwardRef(
    (
        {
            report,
            skill,
            sx,
            disableClick,
            userId,
            userName,
            onClearCache,
            onUpdate,
            ...other
        }: IProps,
        ref: React.Ref<TMatrixCellRef>
    ) => {
        const { openGlobalDrawer } = useGlobalDrawer();
        const location = useLocation();
        const [showDialog, setShowDialog] = useState(false);

        const handleClick = () => {
            if (!report) {
                setShowDialog(true);

                return;
            }

            openGlobalDrawer(
                {
                    type: IGlobalDrawerType.PortfolioItem,
                    itemId: report.id,
                },
                location
            );
        };

        useImperativeHandle(ref, () => ({
            handleClick,
        }));

        return (
            <>
                <TableCell
                    sx={[{ cursor: 'pointer' }, ...withSxProp(sx)]}
                    onClick={!disableClick ? handleClick : undefined}
                    {...other}
                >
                    <PortfolioItemProgressScoreChip
                        hover={!!report}
                        portfolioItem={report}
                    />
                </TableCell>

                <CreateSkillPortfolioItemDialog
                    open={showDialog}
                    skill={skill}
                    userId={userId}
                    userName={userName}
                    onCancel={() => {
                        setShowDialog(false);
                    }}
                    onClose={() => setShowDialog(false)}
                    onUpdate={onUpdate}
                />
            </>
        );
    }
);
